import { FC, memo, useCallback, useRef, useState } from "react";
import VisibilityIcon from "../../icons/visibility-icon.svg";
import VisibilityOffIcon from "../../icons/visibility-off-icon.svg";
import { WithStyle } from "../../utils/WithStyle";
import {
    ErrorMsg,
    InnerWrapper,
    Label,
    PasswordButton,
    RequiredIcon,
    StyledTextInput,
    SuffixWrapper,
    TextInputWrapper,
} from "./TextInput.styled";
import { TextInputProps } from "./types";

const Component: FC<TextInputProps> = memo(
    ({
        label,
        placeholder = "Enter",
        type = "text",
        required = false,
        errorMsg = "",
        labelColor,
        fullWidth = false,
        minWidth = "15rem",
        bgColor,
        suffix,
        maxWidth,
        multiLine,
        onSuffixClick,
        disabled = false,
        ...restProps
    }) => {
        const [focus, setFocus] = useState<boolean>(false),
            [showPass, setShowPass] = useState<boolean>(false),
            inputRef = useRef<HTMLInputElement>(null);

        const handleSuffixClick: React.MouseEventHandler<HTMLDivElement> = (
            event
        ) => {
            onSuffixClick && onSuffixClick(event);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        };
        const onFocus = () => setFocus(true);
        const onBlur = () => setFocus(false);
        const handlePasswordButtonClick = () => setShowPass(!showPass);
        const handleWrapperClick = useCallback(
            () => !disabled && inputRef.current?.focus(),
            [inputRef, disabled]
        );

        return (
            <TextInputWrapper
                focus={focus || showPass}
                error={!!errorMsg}
                fullWidth={fullWidth}
                minWidth={minWidth}
                maxWidth={maxWidth}
                disabled={disabled}
            >
                {label && (
                    <Label htmlFor={`${label}-input`} color={labelColor}>
                        {label} {required && <RequiredIcon>*</RequiredIcon>}
                    </Label>
                )}
                <InnerWrapper
                    id="textInput-inner-wrapper"
                    focus={focus || showPass}
                    error={!!errorMsg}
                    bgColor={bgColor}
                    onClick={handleWrapperClick}
                >
                    <StyledTextInput
                        id={`${label}-input`}
                        type={showPass ? "text" : type}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        ref={inputRef}
                        as={multiLine ? "textarea" : "input"}
                        multiLine={multiLine}
                        bgColor={bgColor}
                        disabled={disabled}
                        {...{ placeholder, ...restProps }}
                    />
                    {(() => {
                        if (type === "password") {
                            return (
                                <PasswordButton
                                    src={
                                        showPass
                                            ? VisibilityOffIcon
                                            : VisibilityIcon
                                    }
                                    alt="Password Icon"
                                    onClick={handlePasswordButtonClick}
                                />
                            );
                        }
                        if (suffix) {
                            return (
                                <SuffixWrapper onClick={handleSuffixClick}>
                                    {suffix}
                                </SuffixWrapper>
                            );
                        }
                    })()}
                </InnerWrapper>
                {errorMsg && <ErrorMsg variant="error">{errorMsg}</ErrorMsg>}
            </TextInputWrapper>
        );
    }
);

export const TextInput: FC<TextInputProps> & WithStyle = Object.assign(
    Component,
    {
        Style: TextInputWrapper,
    }
);
